import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { BookOpen, Globe, CalendarDays, FileText, ArrowRight } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { SEO } from "../components/SEO";
import { MagazineCard, Stars } from "../components/site/MagazineCard";
import { NotFound } from "../components/site/NotFound";
import { SafeImg } from "../components/site/SafeImg";

const REVIEWS = [
  { name: "Verified Reader", rating: 5, text: "Excellent print quality and always on time. The digital flip edition is a lovely bonus." },
  { name: "Verified Reader", rating: 4, text: "Great editorial content. Renewal was effortless through BookMala." },
];

export default function MagazineDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [planId, setPlanId] = useState("");
  const [gallery, setGallery] = useState(0);
  const [tab, setTab] = useState("description");

  useEffect(() => {
    setData(null); setNotFound(false); setGallery(0); setTab("description");
    api.get(`/magazines/${slug}`).then((d) => {
      if (!d || !d.magazine) { setNotFound(true); return; }
      setData(d);
      if (d.plans.length) setPlanId(d.plans[d.plans.length - 1].id);
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound title="Magazine not found" message="We couldn't find this title. It may have been removed or the link is incorrect." testId="magazine-not-found" />;
  if (!data) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 md:py-16 animate-pulse" data-testid="detail-loading">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5"><div className="w-full aspect-[3/4] bg-bm-sand/60 rounded-xl" /></div>
        <div className="lg:col-span-7 space-y-4">
          <div className="h-12 bg-bm-sand/60 rounded w-2/3" />
          <div className="h-4 bg-bm-sand/60 rounded w-1/3" />
          <div className="h-24 bg-bm-sand/60 rounded" />
          <div className="h-40 bg-bm-sand/60 rounded-xl" />
        </div>
      </div>
    </div>
  );
  const { magazine: m, publisher, plans, related } = data;
  const images = [m.cover_image, ...(m.sample_images || []).filter((u) => u !== m.cover_image)];
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Product", name: m.title, image: imgUrl(m.cover_image),
    description: m.short_description, brand: publisher?.name,
    aggregateRating: { "@type": "AggregateRating", ratingValue: m.rating, reviewCount: m.rating_count },
    offers: plans.map((p) => ({ "@type": "Offer", name: p.name, price: p.price, priceCurrency: "INR", availability: "https://schema.org/InStock" })),
  };

  return (
    <div data-testid="magazine-detail-page">
      <SEO title={m.seo_title || m.title} description={m.seo_description || m.short_description} image={m.cover_image} path={`/magazines/${slug}`} type="product" jsonLd={jsonLd} />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
          <div className="bm-card p-6 sm:p-10" data-testid="cover-gallery">
            <SafeImg src={imgUrl(images[gallery])} alt={m.title} fallbackLabel={m.title} className="w-full max-h-[70vh] object-contain rounded-lg shadow-cover" data-testid="gallery-main-image" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((u, i) => (
                <button key={u} onClick={() => setGallery(i)} data-testid={`gallery-thumb-${i}`}
                  className={`w-20 aspect-[3/4] rounded-lg overflow-hidden border-2 transition-colors duration-200 ${i === gallery ? "border-bm-terra" : "border-transparent"}`}>
                  <SafeImg src={imgUrl(u)} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex flex-wrap gap-2 mb-4">
            <Link to={`/magazines?category=${m.category}`} className="chip hover:bg-bm-sand-dark" data-testid="detail-category">
              {m.category.replace(/-/g, " ")}
            </Link>
            {m.featured && <span className="chip chip-active">New Issue</span>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-bm-ink" data-testid="detail-title">{m.title}</h1>
          {publisher && (
            <Link to={`/publishers/${publisher.slug}`} className="inline-block mt-3 text-sm text-bm-terra font-semibold hover:text-bm-terra-dark transition-colors duration-200" data-testid="detail-publisher-link">
              by {publisher.name}
            </Link>
          )}
          <div className="flex items-center gap-3 mt-3">
            <Stars value={m.rating} />
            <span className="text-sm text-bm-muted" data-testid="detail-rating">{m.rating} · {(m.rating_count || 0).toLocaleString("en-IN")} ratings</span>
          </div>
          <p className="mt-6 text-bm-body leading-relaxed" data-testid="detail-short-desc">{m.short_description}</p>

          <div className="grid grid-cols-3 gap-4 mt-8 text-sm">
            <div className="flex items-center gap-2 text-bm-muted"><CalendarDays className="w-4 h-4 text-bm-terra" /> {m.frequency}</div>
            <div className="flex items-center gap-2 text-bm-muted"><Globe className="w-4 h-4 text-bm-terra" /> {m.language}</div>
            {m.issn && <div className="flex items-center gap-2 text-bm-muted"><FileText className="w-4 h-4 text-bm-terra" /> {m.issn}</div>}
          </div>

          <div className="mt-10 border-t border-bm-line pt-8">
            <h3 className="font-display font-semibold text-2xl mb-5 text-bm-ink">Choose your subscription</h3>
            <div className="space-y-3" data-testid="plan-options">
              {plans.map((p) => {
                const perMonth = p.duration_value ? Math.round(p.price / p.duration_value) : p.price;
                return (
                  <label key={p.id} data-testid={`plan-option-${p.id}`}
                    className={`flex items-center justify-between gap-4 bm-card !shadow-none border rounded-xl p-5 cursor-pointer transition-colors duration-200 ${planId === p.id ? "!border-bm-terra bg-bm-sand/30" : "border-bm-line hover:border-bm-sand-dark"}`}>
                    <div className="flex items-center gap-3">
                      <input type="radio" name="plan" checked={planId === p.id} onChange={() => setPlanId(p.id)} className="accent-bm-terra" data-testid={`plan-radio-${p.id}`} />
                      <div>
                        <div className="font-semibold text-sm text-bm-ink">{p.name}</div>
                        <div className="text-xs text-bm-muted">{p.duration_value} {p.duration_unit} · ₹{perMonth}/mo</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-xl text-bm-ink">₹{p.price.toLocaleString("en-IN")}</div>
                      {p.original_price > p.price && <div className="text-xs text-bm-muted line-through">₹{p.original_price.toLocaleString("en-IN")}</div>}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              <button onClick={() => planId && navigate(`/checkout/${planId}`)} data-testid="subscribe-now-btn" className="btn-primary">
                Subscribe Now <ArrowRight className="w-4 h-4" />
              </button>
              <Link to={`/read/${m.slug}`} data-testid="read-sample-btn" className="btn-secondary">
                <BookOpen className="w-4 h-4" /> Read Sample
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 mt-4">
        <div className="flex gap-6 border-b border-bm-line" data-testid="detail-tabs">
          {[["description", "Description"], ["toc", "In This Issue"], ["publisher", "Publisher"], ["reviews", "Reviews"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`pb-3 text-sm font-semibold uppercase tracking-[0.1em] transition-colors duration-200 ${tab === k ? "text-bm-terra border-b-2 border-bm-terra" : "text-bm-muted hover:text-bm-ink"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="py-10 max-w-3xl" data-testid="tab-content">
          {tab === "description" && <p className="text-bm-body leading-relaxed whitespace-pre-line">{m.long_description}</p>}
          {tab === "toc" && (
            <ul className="space-y-3 text-bm-body">
              {["Cover story - the big feature of this edition", "Editor's letter and columns", "Reader letters and expert Q&A", "Special reports, reviews and listings"].map((t) => (
                <li key={t} className="flex gap-3"><span className="text-bm-terra">—</span>{t}</li>
              ))}
            </ul>
          )}
          {tab === "publisher" && publisher && (
            <div>
              <h4 className="font-display font-semibold text-2xl text-bm-ink">{publisher.name}</h4>
              <p className="text-bm-body mt-3 leading-relaxed">{publisher.about}</p>
              <Link to={`/publishers/${publisher.slug}`} className="inline-block mt-4 text-sm font-semibold text-bm-terra hover:text-bm-terra-dark transition-colors duration-200" data-testid="tab-publisher-link">View publisher profile</Link>
            </div>
          )}
          {tab === "reviews" && (
            <div className="space-y-6">
              {REVIEWS.map((r, i) => (
                <div key={i} className="border-b border-bm-line pb-6" data-testid={`review-${i}`}>
                  <Stars value={r.rating} />
                  <p className="text-sm text-bm-body mt-2 leading-relaxed">{r.text}</p>
                  <p className="text-xs text-bm-muted mt-1">{r.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 md:py-16" data-testid="related-section">
          <span className="eyebrow">Keep Reading</span>
          <h2 className="font-display text-3xl md:text-4xl font-semibold mt-3 mb-10 text-bm-ink">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {related.slice(0, 4).map((r) => <MagazineCard key={r.id} mag={r} showPublisher={false} />)}
          </div>
        </div>
      )}
    </div>
  );
}
